/**
 * «Операции не синхронизированы» — the outbox v2 banner (ATL-1445 stage 3, P3; plan §2 "Alerting").
 * Modelled on New Sale's unfiscalized-receipts banner. WHEN it shows is `outboxBannerFor`
 * (`global/Write/outboxBanner.ts`); this file only renders it and keeps it current.
 *
 * MOUNTED since P9b: `SessionOutboxV2Banner` (below) on New Sale and Shift follows the session's
 * drainer for the signed-in organization (`subscribeSessionOutboxV2`) — the drainer starts after the
 * screens mount, from the pin's callback. With no drainer (every Firestore-master session whose
 * outbox v2 is empty: every organization in production today) it renders nothing.
 */
import React, { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'

import { HintView } from './HintView'
import { outboxBannerFor } from '../global/Write/outboxBanner'
import { getSessionOutboxV2, subscribeSessionOutboxV2 } from '../global/Write/sessionOutboxV2'

/** Re-evaluate the waiting threshold while something is queued; the drainer publishes the rest. */
export const BANNER_TICK_MS = 15_000

export function useOutboxV2Status(drainer) {
  const [status, setStatus] = useState(() => (drainer ? drainer.status() : null))
  useEffect(() => {
    if (!drainer) {
      setStatus(null)
      return undefined
    }
    setStatus(drainer.status())
    return drainer.subscribe(setStatus)
  }, [drainer])
  return status
}

export function OutboxV2Banner({ drainer, now = Date.now, style }) {
  const { t } = useTranslation()
  const status = useOutboxV2Status(drainer)
  const [tick, setTick] = useState(0)
  const queued = Boolean(status && status.depth > 0)
  useEffect(() => {
    if (!queued) return undefined
    const handle = setInterval(() => setTick((n) => n + 1), BANNER_TICK_MS)
    return () => clearInterval(handle)
  }, [queued])

  if (!status) return null
  // `tick` only forces the re-evaluation above; the decision reads the clock.
  void tick
  const banner = outboxBannerFor(status, now())
  if (!banner) return null
  return (
    <View
      testID="outbox-v2-banner"
      accessibilityLiveRegion="polite"
      style={[styles.container, style]}
    >
      <HintView
        appearance={banner.appearance}
        iconName={banner.appearance === 'danger' ? 'alert-triangle-outline' : 'cloud-upload-outline'}
        text={t(banner.textKey, { count: banner.count })}
      />
    </View>
  )
}

/** The session's drainer for `organizationId` (its Firestore id), kept current as it starts and stops. */
export function useSessionOutboxV2(organizationId) {
  const [drainer, setDrainer] = useState(() => (organizationId ? getSessionOutboxV2(organizationId) : null))
  useEffect(() => {
    if (!organizationId) {
      setDrainer(null)
      return undefined
    }
    const update = () => setDrainer(getSessionOutboxV2(organizationId))
    update()
    return subscribeSessionOutboxV2(update)
  }, [organizationId])
  return drainer
}

/** The banner for the signed-in organization's session queue (P9b: New Sale, Shift). */
export function SessionOutboxV2Banner({ organizationId, style }) {
  const drainer = useSessionOutboxV2(organizationId)
  return <OutboxV2Banner drainer={drainer} style={style} />
}

const styles = StyleSheet.create({
  container: { paddingHorizontal: 20, paddingTop: 10 },
})
